import React, { useEffect } from 'react';
import _sortBy from 'lodash/sortBy';
import _get from 'lodash/get';
import { useTranslation } from 'react-i18next';

import {
  BreakpointsDesktop,
  BreakpointsTablet,
  BreakpointsMobileV,
} from '@wix/responsive-editor-symbols';
import {
  DropdownBase,
  TextButton,
  listItemActionBuilder,
  Text,
  Box,
} from '@wix/design-system';
import { ChevronDown } from '@wix/wix-ui-icons-common';
import {
  BREAKPOINT_MAX_VALUE,
  BREAKPOINT_MAX_ID,
  EDITOR_TYPE,
} from '@src/utils/constants';
import { calcBreakpointWidth } from '@src/utils/frameHelpers';
import { ACTIONS, useAppContext } from '@src/components/Contexts/AppContext';
import s from './Breakpoints.scss';
import BreakpointsStudioDesktop from '@src/assets/images/BreakpointsStudioDesktop.svg';
import BreakpointsStudioTablet from '@src/assets/images/BreakpointsStudioTablet.svg';
import BreakpointsStudioMobileV from '@src/assets/images/BreakpointsStudioMobileV.svg';
import _ from 'lodash';
import useBiLogger from '@src/components/Hooks/useBiLogger';

interface IBreakpointsProps {
  breakpoints: any[];
}
const Breakpoints = ({ breakpoints }: IBreakpointsProps) => {
  const { t } = useTranslation();
  const { logGenericClick } = useBiLogger();
  const {
    dispatch,
    state: { currentBreakPoint, editorType },
  } = useAppContext();

  const isSiteEditorTypeStudio = () => editorType === EDITOR_TYPE.STUDIO;
  function breakpointsRange() {
    const filteredBreakpointsValues = _.filter(
      breakpoints,
      breakpoint => breakpoint.max < 9999,
    );
    const sorted = _sortBy(filteredBreakpointsValues, bp => bp.max || Infinity);
    const documentBreakpoints = sorted
      .map((bp, index) => {
        if (index !== 0) {
          const prevMax = sorted[index - 1].max;
          return { ...bp, min: prevMax + 1 };
        }
        return bp;
      })
      .reverse();
    documentBreakpoints.unshift({
      min: _get(documentBreakpoints[0], 'max', 0) + 1,
      max: BREAKPOINT_MAX_VALUE,
      id: BREAKPOINT_MAX_ID,
    });
    return documentBreakpoints;
  }

  useEffect(() => {
    dispatch({
      type: ACTIONS.SET_CURRENT_BREAKPOINT,
      payload: breakpointsRange()[0],
    });
  }, []);

  function isSelected(id: string) {
    return currentBreakPoint.id === id;
  }

  function renderBreakPointSVG(max: number, id: string) {
    if (max === BREAKPOINT_MAX_VALUE) {
      if (isSiteEditorTypeStudio()) {
        return (
          <BreakpointsStudioDesktop
            className={isSelected(BREAKPOINT_MAX_ID) ? s.selected : ''}
          />
        );
      }
      return (
        <BreakpointsDesktop
          className={isSelected(BREAKPOINT_MAX_ID) ? s.selected : ''}
        />
      );
    } else if (max <= 758) {
      if (isSiteEditorTypeStudio()) {
        return (
          <BreakpointsStudioMobileV
            className={isSelected(BREAKPOINT_MAX_ID) ? s.selected : ''}
          />
        );
      }
      return (
        <BreakpointsMobileV className={isSelected(id) ? s.selected : ''} />
      );
    } else if (max < 1240) {
      if (isSiteEditorTypeStudio()) {
        return (
          <BreakpointsStudioTablet
            className={isSelected(BREAKPOINT_MAX_ID) ? s.selected : ''}
          />
        );
      }
      return <BreakpointsTablet className={isSelected(id) ? s.selected : ''} />;
    } else {
      if (isSiteEditorTypeStudio()) {
        return (
          <BreakpointsStudioDesktop
            className={isSelected(BREAKPOINT_MAX_ID) ? s.selected : ''}
          />
        );
      }
      return (
        <BreakpointsDesktop className={isSelected(id) ? s.selected : ''} />
      );
    }
  }

  function renderBreakPointTitle(max: number) {
    if (max === BREAKPOINT_MAX_VALUE) {
      return t('get_feedback_header_breakpoints_desktop');
    } else if (max <= 758) {
      return t('get_feedback_header_breakpoints_mobile');
    } else if (max < 1240) {
      return t('get_feedback_header_breakpoints_tablet');
    } else {
      return t('get_feedback_header_breakpoints_desktop');
    }
  }

  const getActionTypeForBi = (max: number) => {
    if (max === BREAKPOINT_MAX_VALUE) {
      return 'desktop_view';
    } else if (max <= 758) {
      return 'tablet_view';
    } else if (max < 1240) {
      return 'mobile_view';
    } else {
      return 'desktop_view';
    }
  };

  function getBreakPointsOptions() {
    return breakpointsRange().map(({ max, min, id, canvasSize }) => {
      const breakpointTitle = renderBreakPointTitle(max);
      return listItemActionBuilder({
        size: 'medium',
        id,
        skin: 'dark',
        className: isSiteEditorTypeStudio() ? s.rootStudioBGColor : '',
        prefixIcon: renderBreakPointSVG(max, id),
        onClick: () => {
          logGenericClick({
            click_type: getActionTypeForBi(max),
          });
          dispatch({
            type: ACTIONS.SET_CURRENT_BREAKPOINT,
            payload: { max, min, id, canvasSize },
          });
        },
        title: isSiteEditorTypeStudio()
          ? ((
              <Box className={s.breakpointsDropdown}>
                <Text size="small" weight="normal" className={s.whiteTextColor}>
                  {breakpointTitle}
                </Text>
                <Text
                  size="small"
                  weight="normal"
                  className={`${s.studioWindowDataTextColor} ${s.alignSelfCenter}`}
                >
                  W {max === BREAKPOINT_MAX_VALUE ? window.innerWidth : max}
                </Text>
              </Box>
            ) as any)
          : ((<>{breakpointTitle}</>) as any),
      });
    });
  }

  function getSelectedBreakPointSVG() {
    const { max, id } = currentBreakPoint;
    return renderBreakPointSVG(max, id);
  }

  function getSelectedBreakPointTitle() {
    const { max } = currentBreakPoint;
    return renderBreakPointTitle(max);
  }

  function getCurrentBreakpointWidth() {
    const breakpointWidth = calcBreakpointWidth({ ...currentBreakPoint });

    return currentBreakPoint.max === BREAKPOINT_MAX_VALUE
      ? window.innerWidth
      : breakpointWidth;
  }

  return (
    <>
      <ul
        className={`${s.breakpointsPreviewBar} ${
          isSiteEditorTypeStudio() ? s.studioBreakpointsPreviewBar : ''
        }`}
        data-hook="breakpoint-preview-bar"
      >
        <li>
          <div
            className={isSiteEditorTypeStudio() ? s.studioDivider : s.divider}
          ></div>
        </li>
        <li
          style={{
            width: isSiteEditorTypeStudio() ? '214px' : '131px',
          }}
        >
          <DropdownBase
            data-hook="story-dropdown-base-uncontrolled-click"
            options={getBreakPointsOptions()}
            placement="bottom"
            className={isSiteEditorTypeStudio() ? s.DropdownBase : ''}
          >
            {({ toggle }) => {
              return (
                <div
                  style={
                    isSiteEditorTypeStudio()
                      ? {
                          display: 'flex',
                          alignItems: 'center',
                          paddingLeft: '17px',
                          paddingRight: '7px',
                          height: '44px',
                        }
                      : {}
                  }
                  onClick={() => {
                    logGenericClick({
                      click_type: 'display_mode_header',
                    });
                    toggle();
                  }}
                >
                  <TextButton
                    size={isSiteEditorTypeStudio() ? 'small' : 'medium'}
                    weight="normal"
                    skin={isSiteEditorTypeStudio() ? 'light' : 'dark'}
                    suffixIcon={
                      isSiteEditorTypeStudio() ? undefined : <ChevronDown />
                    }
                    prefixIcon={
                      isSiteEditorTypeStudio() ? (
                        <Box width="24px" height="24px">
                          {getSelectedBreakPointSVG()}
                        </Box>
                      ) : (
                        getSelectedBreakPointSVG()
                      )
                    }
                    dataHook="drop-down-opener"
                  >
                    {getSelectedBreakPointTitle()}
                  </TextButton>
                  {isSiteEditorTypeStudio() && (
                    <>
                      <Box width="8px"></Box>
                      <Text
                        size="small"
                        weight="normal"
                        className={
                          isSiteEditorTypeStudio()
                            ? s.studioWindowDataTextColor
                            : ''
                        }
                      >
                        W
                      </Text>
                      <Text
                        size="small"
                        weight="normal"
                        className={`${s.widthTitle} ${
                          isSiteEditorTypeStudio()
                            ? s.studioWindowDataTextColor
                            : ''
                        }`}
                      >
                        {getCurrentBreakpointWidth()}
                        <span style={{ paddingTop: '4px' }}>px</span>
                      </Text>
                      <ChevronDown color="rgba(255, 255, 255, 0.70)" />
                    </>
                  )}
                </div>
              );
            }}
          </DropdownBase>
        </li>
        {isSiteEditorTypeStudio() ? null : (
          <>
            <li>
              <div
                className={
                  isSiteEditorTypeStudio() ? s.studioDivider : s.divider
                }
              ></div>
            </li>
            <li>
              <Text>W</Text>
            </li>
            <li style={{ width: '117px', paddingRight: '7px' }}>
              <Text className={s.widthTitle}>
                {getCurrentBreakpointWidth()}
                <span style={{ paddingTop: '4px' }}>px</span>
              </Text>
            </li>
          </>
        )}
        <li>
          <div
            className={isSiteEditorTypeStudio() ? s.studioDivider : s.divider}
          ></div>
        </li>
      </ul>
    </>
  );
};

export default Breakpoints;
